import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  CampaignOutlined,
  ContentCopyOutlined,
  ExpandMoreOutlined,
  HelpOutlineOutlined,
  LinkOutlined,
  RefreshOutlined,
  VpnKeyOutlined,
} from "@mui/icons-material";
import { makeStyles } from "tss-react/mui";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "../../services/toast";
import SettingsCard from "../Settings/SettingsCard";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
  },
  sectionTitle: {
    fontSize: "0.75rem",
    fontWeight: 700,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
  },
  group: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: theme.spacing(2),
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "1fr",
    },
  },
  mono: {
    fontFamily: "monospace",
    fontSize: "0.85rem",
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    paddingTop: theme.spacing(1),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  helper: {
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
  },
  accordion: {
    "&:before": { display: "none" },
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
  },
  accordionSummary: {
    fontSize: "0.85rem",
    fontWeight: 600,
  },
  steps: {
    margin: 0,
    paddingLeft: theme.spacing(2.5),
    fontSize: "0.82rem",
    color: theme.palette.text.secondary,
    "& li": {
      marginBottom: theme.spacing(0.75),
    },
  },
}));

const SETTING_KEYS = {
  key: "googleAdsKey",
  whatsappId: "googleAdsWhatsappId",
  queueId: "googleAdsQueueId",
};

const PHONE_CHANNELS = ["whatsapp", "whatsapp_cloud"];

const webhookUrl = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/$/, "")}/webhook/googleads`;

const generateKey = () => {
  const bytes = new Uint8Array(24);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
};

const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(i18n.t("settings.googleAds.forms.copied"));
  } catch (err) {
    toast.error(i18n.t("settings.googleAds.forms.copyError"));
  }
};

const startIcon = (Icon) => (
  <InputAdornment position="start">
    <Icon fontSize="small" color="disabled" />
  </InputAdornment>
);

const CopyButton = ({ text }) => (
  <Tooltip title={i18n.t("settings.googleAds.forms.copy")}>
    <span>
      <IconButton size="small" disabled={!text} onClick={() => copyToClipboard(text)}>
        <ContentCopyOutlined fontSize="small" />
      </IconButton>
    </span>
  </Tooltip>
);

const GoogleAdsSettings = () => {
  const { classes } = useStyles();

  const [googleKey, setGoogleKey] = useState("");
  const [whatsappId, setWhatsappId] = useState("");
  const [queueId, setQueueId] = useState("");
  const [whatsapps, setWhatsapps] = useState([]);
  const [queues, setQueues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [{ data: settings }, { data: connections }, { data: queueList }] = await Promise.all([
          api.get("/settings"),
          api.get("/whatsapp/"),
          api.get("/queue"),
        ]);
        const list = Array.isArray(settings) ? settings : [];
        const valueOf = (key) => list.find((s) => s.key === key)?.value || "";

        setGoogleKey(valueOf(SETTING_KEYS.key) || generateKey());
        setWhatsappId(valueOf(SETTING_KEYS.whatsappId));
        setQueueId(valueOf(SETTING_KEYS.queueId));
        setWhatsapps(
          (Array.isArray(connections) ? connections : []).filter((w) =>
            PHONE_CHANNELS.includes(w.channel)
          )
        );
        setQueues(Array.isArray(queueList) ? queueList : []);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleRegenerate = () => {
    setGoogleKey(generateKey());
    toast.info(i18n.t("settings.googleAds.forms.regenerated"));
  };

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);

    try {
      await api.put(`/settings/${SETTING_KEYS.key}`, { value: googleKey });
      await api.put(`/settings/${SETTING_KEYS.whatsappId}`, { value: String(whatsappId || "") });
      await api.put(`/settings/${SETTING_KEYS.queueId}`, { value: String(queueId || "") });
      toast.success(i18n.t("settings.googleAds.forms.saved"));
    } catch (err) {
      toastError(err);
    }

    setSaving(false);
  };

  return (
    <SettingsCard
      icon={CampaignOutlined}
      title={i18n.t("settings.googleAds.forms.title")}
      subtitle={i18n.t("settings.googleAds.forms.subtitle")}
    >
      <Box className={classes.root}>
        <Accordion className={classes.accordion} elevation={0} disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreOutlined />}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <HelpOutlineOutlined fontSize="small" color="action" />
              <Typography className={classes.accordionSummary}>
                {i18n.t("settings.googleAds.forms.howTo")}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <ol className={classes.steps}>
              <li>{i18n.t("settings.googleAds.forms.step1")}</li>
              <li>{i18n.t("settings.googleAds.forms.step2")}</li>
              <li>{i18n.t("settings.googleAds.forms.step3")}</li>
              <li>{i18n.t("settings.googleAds.forms.step4")}</li>
              <li>{i18n.t("settings.googleAds.forms.step5")}</li>
            </ol>
            <Typography className={classes.helper} sx={{ mt: 1 }}>
              {i18n.t("settings.googleAds.forms.cloudNote")}
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}>{i18n.t("settings.googleAds.forms.sectionGoogle")}</Typography>
          <div className={classes.group}>
            <TextField
              label={i18n.t("settings.googleAds.forms.webhookUrl")}
              size="small"
              fullWidth
              value={webhookUrl}
              helperText={i18n.t("settings.googleAds.forms.webhookUrlHelp")}
              slotProps={{
                input: {
                  readOnly: true,
                  startAdornment: startIcon(LinkOutlined),
                  endAdornment: (
                    <InputAdornment position="end">
                      <CopyButton text={webhookUrl} />
                    </InputAdornment>
                  ),
                },
              }}
            />

            <TextField
              label={i18n.t("settings.googleAds.forms.key")}
              size="small"
              fullWidth
              value={googleKey}
              helperText={i18n.t("settings.googleAds.forms.keyHelp")}
              slotProps={{
                input: {
                  readOnly: true,
                  className: classes.mono,
                  startAdornment: startIcon(VpnKeyOutlined),
                  endAdornment: (
                    <InputAdornment position="end">
                      <CopyButton text={googleKey} />
                      <Tooltip title={i18n.t("settings.googleAds.forms.regenerate")}>
                        <IconButton size="small" onClick={handleRegenerate}>
                          <RefreshOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                },
              }}
            />
          </div>
        </Box>

        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}>{i18n.t("settings.googleAds.forms.sectionDestination")}</Typography>
          <div className={classes.group}>
            <TextField
              select
              label={i18n.t("settings.googleAds.forms.connection")}
              size="small"
              fullWidth
              value={whatsappId}
              onChange={(e) => setWhatsappId(e.target.value)}
              helperText={i18n.t("settings.googleAds.forms.connectionHelp")}
              slotProps={{ inputLabel: { shrink: true }, select: { displayEmpty: true } }}
            >
              <MenuItem value="">{i18n.t("settings.googleAds.forms.defaultConnection")}</MenuItem>
              {whatsapps.map((w) => (
                <MenuItem key={w.id} value={String(w.id)}>
                  {w.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label={i18n.t("settings.googleAds.forms.queue")}
              size="small"
              fullWidth
              value={queueId}
              onChange={(e) => setQueueId(e.target.value)}
              helperText={i18n.t("settings.googleAds.forms.queueHelp")}
              slotProps={{ inputLabel: { shrink: true }, select: { displayEmpty: true } }}
            >
              <MenuItem value="">{i18n.t("settings.googleAds.forms.noQueue")}</MenuItem>
              {queues.map((q) => (
                <MenuItem key={q.id} value={String(q.id)}>
                  {q.name}
                </MenuItem>
              ))}
            </TextField>
          </div>
        </Box>

        <Box className={classes.actions}>
          <Button
            variant="contained"
            color="primary"
            disabled={loading || saving || !googleKey}
            onClick={handleSave}
          >
            {i18n.t("settings.googleAds.forms.save")}
          </Button>
        </Box>
      </Box>
    </SettingsCard>
  );
};

export default GoogleAdsSettings;
